import { useState } from "react";
import { Outlet, Link, useLocation, useMatch } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ACTIVE_ORG_COOKIE_NAME } from "@/shared/constants";
import { authenticatedBitswanBackendInstance } from "@/lib/api-client";
import { useQuery } from "@tanstack/react-query";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LogOut,
  ChevronUp,
  LayoutDashboard,
  Users,
  UserPlus,
  Globe,
  Eye,
  Building2,
  Plus,
  Check,
  Settings,
  ArrowLeft,
  Mail,
  MessageSquare,
  BadgeCheck,
  Receipt,
  BookMarked, Tag } from "lucide-react";
import NotPublicYetBanner from "@/components/advisor/NotPublicYetBanner";
import ProcessingFeeBanner from "@/components/advisor/ProcessingFeeBanner";
import { useChat } from "@/context/ChatProvider";
import AdvisorChatPanel from "@/components/chat/AdvisorChatPanel";
import { initials } from "@/lib/initials";
import { advisorClientSections } from "@/components/advisor/client/clientSections";
import ClientSwitcher from "@/components/advisor/client/ClientSwitcher";
// ─── Global navigation (when NOT inside a client) ───
const globalNavDefs = [
  { title: "Dashboard", path: "/advisor", icon: LayoutDashboard, desc: "Your clients at a glance" },
  { title: "Clients", path: "/advisor/clients", icon: Users, desc: "The companies you advise" },
  { title: "Messages", path: "/advisor/messages", icon: MessageSquare, desc: "Talk to the clients you advise" },
  { title: "Public profile", path: "/advisor/profile", icon: Globe, desc: "How prospective clients find you" },
  { title: "Public offers", path: "/advisor/public-offers", icon: Tag, desc: "Packages any new client can ask for" },
  { title: "Certification", path: "/advisor/certification", icon: BadgeCheck, desc: "Training, then the call that lists you publicly" },
  { title: "Blog", path: "/advisor/blog", icon: Eye, desc: "Articles on your public profile" },
  { title: "Mailing list", path: "/advisor/mailing-list", icon: Mail, desc: "Subscribers and newsletters" },
  { title: "Billing", path: "/advisor/billing", icon: Receipt, desc: "What clients have paid through BitSwan, and payouts" },
  { title: "Team", path: "/advisor/team", icon: UserPlus, desc: "People in your advisory organisation" },
  { title: "Organisation", path: "/advisor/org-settings", icon: Settings, desc: "Billing details and organisation settings" },
];

export default function AdvisorPortalLayout() {
  const { user, logout } = useAuth();
  const { unread: chatUnread } = useChat();
  const location = useLocation();
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createOrgError, setCreateOrgError] = useState("");

  // The AOC already tracks which organisation you are working in, in a cookie.
  // Read that rather than keeping a second copy of the answer.
  const activeOrgId = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${ACTIVE_ORG_COOKIE_NAME}=`))
    ?.split("=")[1] || "";
  const orgs = (user?.groups || []).filter((g) => g.path.split('/').length === 2);
  const activeOrgName = orgs.find((o: { id: string }) => o.id === activeOrgId)?.name || "";

  // Detect if we're inside a client page: /clients/:id/...
  const clientMatch = useMatch("/advisor/clients/:id/*");
  const clientId = clientMatch?.params.id;
  const isInsideClient = !!clientId;

  // Fetch client name when inside a client
  const { data: clientData } = useQuery({
    queryKey: ["advisor-client-nav", clientId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get(`/advisor/clients/${clientId}/`);
      return res.data as { company_name: string; status_display: string; customer_organization_id: string | null };
    },
    enabled: isInsideClient,
  });

  const setActiveOrg = (id: string) => {
    document.cookie = `${ACTIVE_ORG_COOKIE_NAME}=${id}; path=/; max-age=${60 * 60 * 24 * 30}`;
  };

  const switchOrg = (org: { id: string; name: string }) => {
    setActiveOrg(org.id);
    window.location.reload();
  };

  const clientOrgId = isInsideClient ? clientData?.customer_organization_id ?? null : null;

  const openClientAoc = () => {
    if (!clientOrgId) return;
    setActiveOrg(clientOrgId);
    window.location.assign("/dashboard");
  };

  const createOrg = async () => {
    if (!newOrgName.trim() || creating) return;
    setCreating(true);
    setCreateOrgError("");
    try {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post("/advisor/organizations/", { name: newOrgName.trim() });
      const { id } = res.data;
      setActiveOrg(id);
      setShowCreateOrg(false);
      setNewOrgName("");
      window.location.reload();
    } catch (e: any) {
      setCreateOrgError(
        e?.response?.data?.error || "Could not create the organisation. Please try again.",
      );
    } finally {
      setCreating(false);
    }
  };

  const displayOrgName = (name: string) => name.replace(/^Advisor - /, "");

  // Fetch new lead count for sidebar badge
  const { data: leadCount } = useQuery({
    queryKey: ['lead-count'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/clients/', { params: { status: 'lead' } });
      return (res.data as any[]).length;
    },
    refetchInterval: 30000, // Poll every 30s
    enabled: !isInsideClient,
  });

  // Determine the current page title
  const getPageTitle = () => {
    if (isInsideClient && clientData) {
      return clientData.company_name;
    }
    const navItem = globalNavDefs.find((item) => item.path === location.pathname);
    return navItem ? navItem.title : "Advisor";
  };

  // Get the active sub-path within the client (e.g., "organization", "processes")
  const clientSubPath = clientMatch?.params["*"] || "overview";

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader className="border-b border-sidebar-border">
          <div className="flex flex-col items-center px-2 py-3">
            <Link to="/advisor/dashboard" className="mb-2">
              <img src="/bitswan-logo-dark.svg" alt="BitSwan" className="h-8" />
            </Link>
            <div className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4 text-amber-500" />
              <span className="font-semibold text-xs text-muted-foreground">{"Advisor"}</span>
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <ScrollArea className="h-full">
            {/* Organization Switcher — always visible */}
            <SidebarGroup>
              <SidebarGroupLabel>{"Organization"}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton data-testid="org-switcher" aria-label="Switch organization">
                          <Building2 className="h-4 w-4" />
                          <span className="truncate">{displayOrgName(activeOrgName) || "Organization"}</span>
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="start" sideOffset={4} className="w-56">
                        {orgs.map((org) => (
                          <DropdownMenuItem key={org.id} onClick={() => switchOrg(org)} className={org.id === activeOrgId ? "bg-accent" : ""} data-org-id={org.id}>
                            <div className="flex items-center gap-2 w-full">
                              {org.id === activeOrgId && <Check className="h-3 w-3 shrink-0" />}
                              <span className="truncate">{displayOrgName(org.name)}</span>
                            </div>
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowCreateOrg(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          {"Create Organization"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                  {showCreateOrg && (
                    <div className="px-2 py-2 space-y-2">
                      <Input placeholder={"Organization name"} value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createOrg()} data-testid="new-org-name" autoFocus />
                      {createOrgError && (
                        <p className="text-xs text-red-600" data-testid="new-org-error">{createOrgError}</p>
                      )}
                      <div className="flex gap-1">
                        <Button size="sm" onClick={createOrg} disabled={creating || !newOrgName.trim()} className="flex-1">{"Create"}</Button>
                        <Button size="sm" variant="outline" onClick={() => { setShowCreateOrg(false); setNewOrgName(""); setCreateOrgError(""); }}>{"Cancel"}</Button>
                      </div>
                    </div>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {isInsideClient ? (
              /* ═══════════════ CLIENT-SCOPED NAVIGATION ═══════════════ */
              <>
                {/* Back to clients */}
                <SidebarGroup>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild>
                          <Link to="/advisor/clients" className="text-muted-foreground">
                            <ArrowLeft className="h-4 w-4" />
                            <span>Back to Clients</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>

                {clientId && (
                  <ClientSwitcher clientId={clientId} subPath={clientSubPath} current={clientData} />
                )}

                {/* Phase-based navigation */}
                {advisorClientSections.map((section) => (
                  <SidebarGroup key={section.label}>
                    <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {section.items.map((item) => (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton
                              asChild
                              isActive={clientSubPath === item.path || clientSubPath.startsWith(item.path + "/")}
                            >
                              <Link to={`/advisor/clients/${clientId}/${item.path}`} data-testid={`client-nav-${item.path}`}>
                                <item.icon className="h-4 w-4" />
                                <span>{item.label}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                ))}
              </>
            ) : (
              /* ═══════════════ GLOBAL NAVIGATION ═══════════════ */
              <SidebarGroup>
                <SidebarGroupLabel>Navigation</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {globalNavDefs.map((item) => (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild isActive={location.pathname === item.path} tooltip={item.desc}>
                          <Link to={item.path} className="flex items-center gap-2 w-full" data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-') || 'home'}`}>
                            <item.icon className="h-4 w-4" />
                            <span className="flex-1">{item.title}</span>
                            {item.path === "/advisor/messages" && chatUnread > 0 && (
                              <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="advisor-chat-unread">
                                {chatUnread}
                              </span>
                            )}
                            {item.path === "/advisor/clients" && leadCount != null && leadCount > 0 && (
                              <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="lead-badge">
                                {leadCount}
                              </span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    {clientOrgId ? (
                      <SidebarMenuButton
                        onClick={openClientAoc}
                        tooltip={`Open the AOC as ${clientData?.company_name ?? "this client"}: their servers and people`}
                        data-testid="nav-back-to-aoc"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        <span>Client Automation Center</span>
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton
                        asChild
                        tooltip="Your own organisation: servers and people"
                      >
                        <Link to="/dashboard" data-testid="nav-back-to-aoc">
                          <LayoutDashboard className="h-4 w-4" />
                          <span>Back to the AOC</span>
                        </Link>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="How the advisor portal works, with screenshots">
                      <a href="/handbooks/advisor.html" target="_blank" rel="noreferrer" data-testid="nav-advisor-handbook">
                        <BookMarked className="h-4 w-4" />
                        <span>Handbook</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Language Switcher — always visible */}
          </ScrollArea>
        </SidebarContent>
        <SidebarFooter className="border-t border-sidebar-border">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-amber-500 text-white text-xs">
                        {user?.name ? initials(user.name) : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.name || "User"}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email || ""}</span>
                    </div>
                    <ChevronUp className="ml-auto h-4 w-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>
                  <div className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-amber-500 text-white text-xs">
                        {user?.name ? initials(user.name) : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.name || "User"}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email || ""}</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    {"Sign Out"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">{getPageTitle()}</h1>
          </div>
        </header>
        <AdvisorChatPanel />
        <main className="flex-1 overflow-auto p-6">
          <NotPublicYetBanner />
          <ProcessingFeeBanner />
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

